import { useEffect, useState, useCallback } from "react";
import { API } from "@/lib/constants";
import { AlertTriangle, Ban } from "lucide-react";

/**
 * Budget headroom indicator — admin-only header pill.
 *
 * Replaces per-generation cost displays (May 28 2026, Remi's call).
 * Renders nothing at `silent` state. Surfaces amber pill at 70-90% MTD,
 * red pill at 90-100%, blocked pill at 100% (also enforced server-side
 * via HTTP 429).
 *
 * Polls every 60s. Lightweight — the endpoint aggregates Mongo ledger
 * rows by month_key and returns a single severity flag plus per-subsystem
 * details for the tooltip.
 *
 * Hidden for non-admins because the endpoint is admin-gated; calling
 * it would just 401.
 */
const POLL_MS = 60_000;

export default function BudgetHeadroomIndicator({ user }) {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const isAdmin = (user?.role || "").toLowerCase() === "admin";

  const fetchStatus = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const r = await fetch(`${API}/v2/ai-budget/status`, { credentials: "include" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      setStatus(j);
      setError(null);
    } catch (e) {
      setError(e.message);
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, POLL_MS);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  if (!isAdmin || !status || error) return null;
  if (status.overall_severity === "silent") return null;

  const severityStyle = {
    amber: "bg-amber-100 text-amber-900 border-amber-400",
    red:   "bg-red-100   text-red-900   border-red-400",
    blocked: "bg-red-700 text-white       border-red-900",
  }[status.overall_severity] || "bg-slate-100 text-slate-800 border-slate-300";

  const Icon = status.overall_severity === "blocked" ? Ban : AlertTriangle;

  // Compose a single descriptive label from whichever subsystem is the
  // loudest. Tooltip shows the per-subsystem breakdown.
  const loudest = [...status.subsystems].sort((a, b) => b.percent - a.percent)[0];
  const label = status.overall_severity === "blocked"
    ? `${loudest.subsystem} ceiling reached`
    : `${loudest.subsystem} at ${loudest.percent.toFixed(0)}% of $${loudest.ceiling_usd}/mo`;

  const tooltip = status.subsystems
    .map(s => `${s.subsystem}: $${s.month_to_date_usd.toFixed(2)} / $${s.ceiling_usd} (${s.percent.toFixed(0)}%)`)
    .join("\n");

  return (
    <div
      className={`hidden md:flex items-center gap-1.5 text-[11px] font-medium px-2 py-1 rounded border ${severityStyle}`}
      title={tooltip}
      data-testid={`budget-headroom-${status.overall_severity}`}
    >
      <Icon className="h-3 w-3" />
      <span>{label}</span>
    </div>
  );
}
